import type { DailyStat, DrinkEntry } from "../types/models";
import { normalizeApiLocalDate } from "./dates";
import { getLocalDateInTimezone } from "./timezone";

export type HydrationBucket = "day" | "week" | "month";

export type HydrationTrendPoint = {
  key: string;
  startDate: string;
  endDate: string;
  valueMl: number;
  totalMl: number;
  dayCount: number;
};

export type HydrationTrend = {
  bucket: HydrationBucket;
  points: HydrationTrendPoint[];
  averageMl: number;
  hasData: boolean;
};

const DAY_MS = 86_400_000;

export function buildHydrationTrend(stats: DailyStat[], startDate: string, endDate: string): HydrationTrend {
  const normalizedStart = normalizeDateInput(startDate);
  const normalizedEnd = normalizeDateInput(endDate);
  if (!normalizedStart || !normalizedEnd || compareDates(normalizedStart, normalizedEnd) > 0) {
    return { bucket: "day", points: [], averageMl: 0, hasData: false };
  }

  const valuesByDate = new Map<string, number>();
  stats.forEach((stat) => {
    const date = normalizeApiLocalDate(stat.local_date);
    if (date && compareDates(date, normalizedStart) >= 0 && compareDates(date, normalizedEnd) <= 0) {
      valuesByDate.set(date, stat.hydration_ml ?? 0);
    }
  });

  const dataDates = Array.from(valuesByDate.entries())
    .filter(([, value]) => value > 0)
    .map(([date]) => date)
    .sort(compareDates);
  if (!dataDates.length) {
    return { bucket: "day", points: [], averageMl: 0, hasData: false };
  }

  const effectiveStart = compareDates(dataDates[0] ?? normalizedStart, normalizedStart) > 0 ? dataDates[0] ?? normalizedStart : normalizedStart;
  const lastDataDate = dataDates[dataDates.length - 1] ?? normalizedEnd;
  const effectiveEnd = compareDates(lastDataDate, normalizedEnd) < 0 ? lastDataDate : normalizedEnd;
  const visibleDates = datesBetween(effectiveStart, effectiveEnd);
  const bucket = chooseBucket(visibleDates.length);
  const points = bucket === "day" ? dayPoints(visibleDates, valuesByDate) : bucketedPoints(visibleDates, valuesByDate, bucket);
  const totalMl = visibleDates.reduce((sum, date) => sum + (valuesByDate.get(date) ?? 0), 0);

  return {
    bucket,
    points,
    averageMl: visibleDates.length ? totalMl / visibleDates.length : 0,
    hasData: true
  };
}

export function mergeDailyStatsWithDrinkEntries(stats: DailyStat[], entries: DrinkEntry[], timezone: string): DailyStat[] {
  const statsByDate = new Map<string, DailyStat>();
  stats.forEach((stat) => {
    const date = normalizeApiLocalDate(stat.local_date);
    if (date) {
      statsByDate.set(date, { ...stat, local_date: date });
    }
  });

  const entryTotals = new Map<string, { hydrationMl: number; drinkCount: number }>();
  entries.forEach((entry) => {
    const date = readEntryDate(entry, timezone);
    if (!date) {
      return;
    }
    const current = entryTotals.get(date) ?? { hydrationMl: 0, drinkCount: 0 };
    current.hydrationMl += entry.hydration_amount_ml ?? entry.amount_ml ?? 0;
    current.drinkCount += 1;
    entryTotals.set(date, current);
  });

  entryTotals.forEach((entryTotal, date) => {
    const stat = statsByDate.get(date);
    if (!stat) {
      statsByDate.set(date, {
        drink_count: entryTotal.drinkCount,
        hydration_ml: entryTotal.hydrationMl,
        local_date: date
      });
      return;
    }
    const statHydrationMl = stat.hydration_ml ?? 0;
    if (entryTotal.hydrationMl > statHydrationMl) {
      statsByDate.set(date, {
        ...stat,
        drink_count: Math.max(stat.drink_count ?? 0, entryTotal.drinkCount),
        hydration_ml: entryTotal.hydrationMl,
        local_date: date
      });
    }
  });

  return Array.from(statsByDate.values()).sort((left, right) => compareDates(normalizeApiLocalDate(left.local_date) ?? "", normalizeApiLocalDate(right.local_date) ?? ""));
}

export function dateInputDaysAgo(endDate: string, daysAgo: number): string {
  const end = parseDateInput(endDate);
  end.setUTCDate(end.getUTCDate() - daysAgo);
  return toDateInput(end);
}

export function earliestStatDate(stats: DailyStat[]): string | undefined {
  return stats
    .map((stat) => normalizeApiLocalDate(stat.local_date))
    .filter((date): date is string => Boolean(date))
    .sort(compareDates)[0];
}

function chooseBucket(dayCount: number): HydrationBucket {
  if (dayCount > 370) {
    return "month";
  }
  if (dayCount > 90) {
    return "week";
  }
  return "day";
}

function dayPoints(dates: string[], valuesByDate: Map<string, number>): HydrationTrendPoint[] {
  return dates.map((date) => {
    const valueMl = valuesByDate.get(date) ?? 0;
    return {
      key: date,
      startDate: date,
      endDate: date,
      valueMl,
      totalMl: valueMl,
      dayCount: 1
    };
  });
}

function bucketedPoints(dates: string[], valuesByDate: Map<string, number>, bucket: Exclude<HydrationBucket, "day">): HydrationTrendPoint[] {
  const groups = new Map<string, { dates: string[]; totalMl: number }>();

  dates.forEach((date, index) => {
    const key = bucket === "week" ? `week-${Math.floor(index / 7)}` : date.slice(0, 7);
    const group = groups.get(key) ?? { dates: [], totalMl: 0 };
    group.dates.push(date);
    group.totalMl += valuesByDate.get(date) ?? 0;
    groups.set(key, group);
  });

  return Array.from(groups.entries()).map(([key, group]) => ({
    key,
    startDate: group.dates[0] ?? "",
    endDate: group.dates[group.dates.length - 1] ?? "",
    valueMl: group.dates.length ? group.totalMl / group.dates.length : 0,
    totalMl: group.totalMl,
    dayCount: group.dates.length
  }));
}

function datesBetween(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  const cursor = parseDateInput(startDate);
  const end = parseDateInput(endDate);
  while (cursor.getTime() <= end.getTime()) {
    dates.push(toDateInput(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
}

function normalizeDateInput(value: string): string | undefined {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : normalizeApiLocalDate(value);
}

function readEntryDate(entry: DrinkEntry, timezone: string): string | undefined {
  const localDate = normalizeApiLocalDate(entry.local_date);
  if (localDate) {
    return localDate;
  }
  if (!entry.entry_time) {
    return undefined;
  }
  const parsed = new Date(entry.entry_time);
  return Number.isNaN(parsed.getTime()) ? undefined : getLocalDateInTimezone(parsed, timezone);
}

function compareDates(left: string, right: string): number {
  return parseDateInput(left).getTime() - parseDateInput(right).getTime();
}

function parseDateInput(value: string): Date {
  return new Date(`${value}T00:00:00Z`);
}

function toDateInput(date: Date): string {
  return new Date(Math.floor(date.getTime() / DAY_MS) * DAY_MS).toISOString().slice(0, 10);
}
